import React from 'react';
import { cn } from '../../lib/utils';

export const GlassCard = ({ children, className, hover = false, ...props }) => {
  return (
    <div
      className={cn(
        'bg-white rounded-2xl p-6 shadow-sm border border-gray-100',
        hover && 'card-hover cursor-pointer',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const StatCard = ({ title, value, subtitle, icon: Icon, trend, className }) => {
  return (
    <GlassCard className={cn('relative overflow-hidden', className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 font-mono">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <p className={cn(
              'text-xs mt-2 font-medium',
              trend > 0 ? 'text-emerald-600' : 'text-red-500'
            )}>
              {trend > 0 ? '+' : ''}{trend}% from last month
            </p>
          )}
        </div>
        {Icon && (
          <div className="p-3 rounded-xl bg-[#4E50FF]/10">
            <Icon className="w-6 h-6 text-[#4E50FF]" strokeWidth={1.5} />
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export const StatusBadge = ({ status, className }) => {
  const statusColors = {
    available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    reserved: 'bg-amber-50 text-amber-700 border-amber-200',
    booked: 'bg-blue-50 text-blue-700 border-blue-200',
    sold: 'bg-gray-100 text-gray-600 border-gray-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
    payment_uploaded: 'bg-blue-50 text-blue-700 border-blue-200',
    payment_verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    loan_application: 'bg-purple-50 text-purple-700 border-purple-200',
    loan_approved: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    loan_rejected: 'bg-red-50 text-red-600 border-red-200',
    spa_pending: 'bg-orange-50 text-orange-700 border-orange-200',
    spa_signed: 'bg-violet-50 text-violet-700 border-violet-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
    escalated: 'bg-orange-50 text-orange-700 border-orange-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
    deposit_paid: 'bg-blue-50 text-blue-700 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    under_review: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    revision_requested: 'bg-orange-50 text-orange-700 border-orange-200',
    pic_approved: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    admin_approved: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border capitalize',
        statusColors[status] || 'bg-gray-100 text-gray-500 border-gray-200',
        className
      )}
    >
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

export const RankBadge = ({ rank, className }) => {
  const rankColors = {
    agent: 'bg-gray-100 text-gray-600 border-gray-200',
    neg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rm: 'bg-blue-50 text-blue-700 border-blue-200',
    avp: 'bg-purple-50 text-purple-700 border-purple-200',
    svp: 'bg-orange-50 text-orange-700 border-orange-200',
    evp: 'rank-badge-premium',
  };

  const rankLabels = {
    agent: 'Agent',
    neg: 'NEG',
    rm: 'RM',
    avp: 'AVP',
    svp: 'SVP',
    evp: 'EVP',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider',
        rankColors[rank] || 'bg-gray-100 text-gray-600 border-gray-200',
        className
      )}
    >
      {rankLabels[rank] || rank}
    </span>
  );
};

export default GlassCard;
